/**
 * Tax Expense Tracker - Cloud API Service
 * Connects to Cloudflare Workers backend
 */

import { Expense, DriverSettings, Stats, DEFAULT_SETTINGS } from '../types';

// API base URL - update after deploying your backend
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://tax-expense-tracker-api.mr-doug-sullivan.workers.dev';

// Helper for API requests
async function apiRequest<T>(
  endpoint: string, 
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `API error: ${response.status}`);
  }

  return response.json();
}

// =====================================================
// EXPENSE OPERATIONS
// =====================================================

export async function getAllExpenses(): Promise<Expense[]> {
  try {
    const expenses = await apiRequest<any[]>('/api/expenses');
    // Map cloud format to frontend format
    return expenses.map(mapCloudExpenseToLocal);
  } catch (error) {
    console.error('Failed to fetch expenses:', error);
    throw error;
  }
}

export async function getExpense(id: string): Promise<Expense | undefined> {
  try {
    const expense = await apiRequest<any>(`/api/expenses/${id}`);
    return mapCloudExpenseToLocal(expense);
  } catch (error) {
    console.error('Failed to fetch expense:', error);
    return undefined;
  }
}

export async function addExpense(expense: Expense): Promise<void> {
  try {
    await apiRequest('/api/expenses', {
      method: 'POST',
      body: JSON.stringify({
        id: expense.id,
        category: expense.category,
        subcategory: expense.subcategory,
        description: expense.description,
        amount: expense.amount,
        date: expense.date,
        month: expense.month,
        year: expense.year,
        filename: expense.filename,
        fileData: expense.fileData,  // base64 encoded
        fileType: expense.fileType,
        fileSize: expense.fileSize,
        freightBillNumber: expense.freightBillNumber,
        isSentToBennett: expense.isSentToBennett
      }),
    });
  } catch (error) {
    console.error('Failed to add expense:', error);
    throw error;
  }
}

export async function updateExpense(expense: Expense): Promise<void> {
  try {
    await apiRequest(`/api/expenses/${expense.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        category: expense.category,
        subcategory: expense.subcategory,
        description: expense.description,
        amount: expense.amount,
        date: expense.date,
        month: expense.month,
        year: expense.year,
        filename: expense.filename,
        fileData: expense.fileData,
        fileType: expense.fileType,
        fileSize: expense.fileSize,
        freightBillNumber: expense.freightBillNumber,
        isSentToBennett: expense.isSentToBennett
      }),
    });
  } catch (error) {
    console.error('Failed to update expense:', error);
    throw error;
  }
}

export async function deleteExpense(id: string): Promise<void> {
  try {
    await apiRequest(`/api/expenses/${id}`, {
      method: 'DELETE',
    });
  } catch (error) {
    console.error('Failed to delete expense:', error);
    throw error;
  }
}

export async function markBOLAsSent(id: string): Promise<void> {
  try {
    await apiRequest(`/api/expenses/${id}/sent-to-bennett`, {
      method: 'PATCH',
    });
  } catch (error) {
    console.error('Failed to mark BOL as sent:', error);
    throw error;
  }
}

export async function getExpensesByCategory(category: string): Promise<Expense[]> {
  try {
    const expenses = await apiRequest<any[]>(`/api/expenses/category/${category}`);
    return expenses.map(mapCloudExpenseToLocal);
  } catch (error) {
    console.error('Failed to fetch expenses by category:', error);
    throw error;
  }
}

export async function getExpensesByMonthYear(year: number, month: number): Promise<Expense[]> {
  try {
    const expenses = await apiRequest<any[]>(`/api/expenses/month/${year}/${month}`);
    return expenses.map(mapCloudExpenseToLocal);
  } catch (error) {
    console.error('Failed to fetch expenses by month:', error);
    throw error;
  }
}

// =====================================================
// SETTINGS OPERATIONS
// =====================================================

export async function getSettings(): Promise<DriverSettings> {
  try {
    const settings = await apiRequest<DriverSettings>('/api/settings');
    return settings;
  } catch (error) {
    console.error('Failed to fetch settings:', error);
    return DEFAULT_SETTINGS;
  }
}

export async function saveSettings(settings: DriverSettings): Promise<void> {
  try {
    await apiRequest('/api/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  } catch (error) {
    console.error('Failed to save settings:', error);
    throw error;
  }
}

// =====================================================
// STATS OPERATIONS
// =====================================================

export async function getStats(): Promise<Stats> {
  try {
    return await apiRequest<Stats>('/api/stats');
  } catch (error) {
    console.error('Failed to fetch stats:', error);
    throw error;
  }
}

export async function calculateStorageUsed(): Promise<{ bytes: number; formatted: string }> {
  try {
    const stats = await getStats();
    return {
      bytes: stats.storageUsed,
      formatted: stats.storageUsedFormatted,
    };
  } catch (error) {
    console.error('Failed to calculate storage:', error);
    return { bytes: 0, formatted: '0 KB' };
  }
}

// =====================================================
// FILE OPERATIONS
// =====================================================

export function getFileUrl(fileUrl: string): string {
  if (!fileUrl) return '';
  // If it's already a full URL, return as-is
  if (fileUrl.startsWith('http')) return fileUrl;
  // If it's a data URL, return as-is
  if (fileUrl.startsWith('data:')) return fileUrl;
  // Otherwise, construct the API URL
  const filename = fileUrl.replace('documents/', '');
  return `${API_BASE_URL}/api/files/${filename}`;
}

// =====================================================
// DATA MIGRATION HELPERS
// =====================================================

// Map cloud expense format to local format (handles fileUrl -> fileData conversion)
function mapCloudExpenseToLocal(cloudExpense: any): Expense {
  return {
    id: cloudExpense.id,
    category: cloudExpense.category,
    subcategory: cloudExpense.subcategory,
    description: cloudExpense.description,
    amount: cloudExpense.amount,
    date: cloudExpense.date,
    month: cloudExpense.month,
    year: cloudExpense.year,
    filename: cloudExpense.filename,
    // If there's a fileUrl, we can construct the download URL
    // The fileData field will be populated when needed (e.g., for PDF export)
    fileData: cloudExpense.fileUrl ? getFileUrl(cloudExpense.fileUrl) : '',
    fileType: cloudExpense.fileType,
    fileSize: cloudExpense.fileSize,
    createdAt: cloudExpense.createdAt,
    freightBillNumber: cloudExpense.freightBillNumber,
    isSentToBennett: cloudExpense.isSentToBennett,
  };
}

// Export data as JSON backup (fetches from cloud)
export async function exportData(): Promise<string> {
  const expenses = await getAllExpenses();
  const settings = await getSettings();
  return JSON.stringify({ expenses, settings, exportDate: new Date().toISOString() }, null, 2);
}

// Import data from JSON backup (uploads to cloud)
export async function importData(jsonData: string): Promise<{ imported: number; errors: number }> {
  const data = JSON.parse(jsonData);
  let imported = 0;
  let errors = 0;

  if (data.expenses && Array.isArray(data.expenses)) {
    for (const expense of data.expenses) {
      try {
        await addExpense(expense);
        imported++;
      } catch {
        errors++;
      }
    }
  }

  if (data.settings) {
    await saveSettings(data.settings);
  }

  return { imported, errors };
}

// Health check
export async function checkApiHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/health`);
    return response.ok;
  } catch {
    return false;
  }
}

